import { UserFormSchema } from "../../../common/components/user-inv-modal/api/schema";
import { clientModel } from "../../client/api/types";
import { create } from "zustand";
import { UserManagementModel, PagedUserManagementModel } from "./types";
import { UserModel } from "../../users-facility/api/types";

export interface IUserManagementState {
  initialUserManagementState: PagedUserManagementModel;

  isChangingAdmin: boolean;
  setChangingAdmin: (isChangingAdmin: boolean) => void;

  isEditModalOpen: boolean;
  setEditModalOpen: (isEditModalOpen: boolean) => void;

  isAddModalOpen: boolean;
  setAddModal: (isAddModalOpen: boolean) => void;

  anchorEl: null | HTMLElement;
  setAnchorEl: (anchorEl: null | HTMLElement) => void;

  activeRowId: undefined | string;
  setActiveRowId: (activeRowId: undefined | string) => void;

  filterAnchorEl: null | HTMLElement;
  setFilterAnchorEl: (filterAnchorEl: null | HTMLElement) => void;

  currentStep: number;
  setCurrentStep: (currentStep: number) => void;

  selectedUser?: UserFormSchema;
  setSelectedUser: (user: UserFormSchema | undefined) => void;

  selectedUserId?: string;
  setSelectedUserId: (user?: string) => void;

  selectedFacility?: UserManagementModel;
  setSelectedFacility: (
    selectedFacility: UserManagementModel | undefined,
  ) => void;

  isInitializingFacility: boolean;
  setIsInitializingFacility: (val: boolean) => void;

  facilityFormNeedsReload: boolean;
  setFacilityFormNeedsReload: (val: boolean) => void;

  setUserManagementPage: (pageNumber: number) => void;
  setSearchText: (searchText: string | undefined) => void;
  setFilters: (filters: {
    isShowAll: boolean;
    isEnabled: boolean;
    isDisabled: boolean;
  }) => void;

  isAddGroupAdminModalOpen: boolean;
  setAddGroupAdminModal: (open: boolean) => void;

  selectedGroupClientIds: string[];
  setSelectedGroupClientIds: (ids: string[]) => void;

  isEditGroupAdminModal: boolean;
  setIsEditGroupAdminModal: (val: boolean) => void;

  isLockUserModalOpen: boolean;
  setLockUserModal: (val: boolean) => void;

  isDeleteUserModalOpen: boolean;
  setDeleteUserModal: (val: boolean) => void;

  isAssignProductsModalOpen: boolean;
  setAssignProductsModal: (val: boolean) => void;

  reset: () => void;

  isResetPassModalOpen: boolean;
  setResetPassModal: (val: boolean) => void;

  isConfirmPassModalOpen: boolean;
  setConfirmPassModal: (val: boolean) => void;

  isUserCredentialsSent: boolean;
  setUserCredentialsSentModal: (val: boolean) => void;

  activeUser: UserManagementModel | undefined;
  setActiveUser: (val: UserManagementModel | undefined) => void;

  pendingCreateUser?: Partial<UserFormSchema>;
  setPendingCreateUser: (user?: Partial<UserFormSchema>) => void;
}

const useUserManagementStore = create<IUserManagementState>((set, get) => ({
  initialUserManagementState: {
    items: [],
    totalItems: 0,
    pageNumber: 1,
    pageSize: 10,
    searchText: undefined,
    currentStep: 0,
  },

  isChangingAdmin: false,
  setChangingAdmin: (isChangingAdmin) => set({ isChangingAdmin }),

  isAddModalOpen: false,
  setAddModal: (isAddModalOpen) => set({ isAddModalOpen }),

  isEditModalOpen: false,
  setEditModalOpen: (isEditModalOpen) => set({ isEditModalOpen }),

  anchorEl: null,
  setAnchorEl: (anchorEl) => set({ anchorEl }),

  activeRowId: undefined,
  setActiveRowId: (activeRowId) => set({ activeRowId }),

  filterAnchorEl: null,
  setFilterAnchorEl: (filterAnchorEl) => set({ filterAnchorEl }),

  currentStep: 0,
  setCurrentStep: (currentStep) => set({ currentStep }),

  selectedUser: undefined,
  setSelectedUser: (user: UserFormSchema | undefined) =>
    set({ selectedUser: user }),

  selectedUserId: undefined,
  setSelectedUserId: (selectedUserId) => set({ selectedUserId }),

  selectedFacility: undefined,
  setSelectedFacility: (selectedFacility) => set({ selectedFacility }),

  isInitializingFacility: false,
  setIsInitializingFacility: (isInitializingFacility) =>
    set({ isInitializingFacility }),

  facilityFormNeedsReload: false,
  setFacilityFormNeedsReload: (facilityFormNeedsReload) =>
    set({ facilityFormNeedsReload }),

  setUserManagementPage: (pageNumber: number) =>
    set((state) => ({
      initialUserManagementState: {
        ...state.initialUserManagementState,
        pageNumber,
      },
    })),

  setSearchText: (searchText: string | undefined) =>
    set((state) => ({
      initialUserManagementState: {
        ...state.initialUserManagementState,
        searchText,
      },
    })),

  setFilters: (filters: {
    isShowAll: boolean;
    isEnabled: boolean;
    isDisabled: boolean;
  }) =>
    set((state) => ({
      initialUserManagementState: {
        ...state.initialUserManagementState,
        ...filters,
      },
    })),
  activeUser: undefined,
  setActiveUser: (activeUser) => set({ activeUser }),

  isResetPassModalOpen: false,
  setResetPassModal: (isResetPassModalOpen) => set({ isResetPassModalOpen }),

  isConfirmPassModalOpen: false,
  setConfirmPassModal: (isConfirmPassModalOpen) =>
    set({ isConfirmPassModalOpen }),

  isUserCredentialsSent: false,
  setUserCredentialsSentModal: (isUserCredentialsSent) =>
    set({ isUserCredentialsSent }),

  isAddGroupAdminModalOpen: false,
  setAddGroupAdminModal: (open) => set({ isAddGroupAdminModalOpen: open }),

  selectedGroupClientIds: [],
  setSelectedGroupClientIds: (ids) => set({ selectedGroupClientIds: ids }),

  isEditGroupAdminModal: false,
  setIsEditGroupAdminModal: (isEditGroupAdminModal) =>
    set({ isEditGroupAdminModal }),

  isLockUserModalOpen: false,
  setLockUserModal: (isLockUserModalOpen) => set({ isLockUserModalOpen }),

  isDeleteUserModalOpen: false,
  setDeleteUserModal: (isDeleteUserModalOpen) => set({ isDeleteUserModalOpen }),

  isAssignProductsModalOpen: false,
  setAssignProductsModal: (isAssignProductsModalOpen) => set({ isAssignProductsModalOpen }),

  pendingCreateUser: undefined,
  setPendingCreateUser: (pendingCreateUser) => set({ pendingCreateUser }),

  reset: () =>
    set({
      initialUserManagementState: {
        items: [],
        totalItems: 0,
        pageNumber: 1,
        pageSize: 10,
        searchText: undefined,
      },
      isChangingAdmin: false,
      isAddModalOpen: false,
      isEditModalOpen: false,
      anchorEl: null,
      activeRowId: undefined,
      filterAnchorEl: null,
      currentStep: 0,
      selectedUser: undefined,
      selectedUserId: undefined,
      activeUser: undefined,
      pendingCreateUser: undefined,
      selectedFacility: undefined,
      isInitializingFacility: false,
      facilityFormNeedsReload: false,
      isResetPassModalOpen: false,
      isUserCredentialsSent: false,
      isConfirmPassModalOpen: false,
      isAddGroupAdminModalOpen: false,
      selectedGroupClientIds: [],
      isEditGroupAdminModal: false,
      isLockUserModalOpen: false,
      isDeleteUserModalOpen: false,
      isAssignProductsModalOpen: false,
    }),
}));

export default useUserManagementStore;
